import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { PrimeGlyph } from "./PrimeGlyph";

// PRIMENG `p-multiSelect` (`[filter]="false"`, `[showClear]="true"`) IN THE PILLARS THEMES:
// THE `p-dropdown` FIELD - `--surface-ground`, 1PX `#ced4da` BORDER, 4PX RADIUS, 0.5REM
// 0.75REM LABEL - SHOWING THE SELECTED LABELS COMMA-JOINED (PRIMENG'S DEFAULT `display`),
// A CLEAR `times` ICON WHEN ANYTHING IS SELECTED, AND A CHECKBOX PER OPTION. DISABLED IS
// PRIMENG'S 0.6 OPACITY.
export function PMultiSelect<V extends string | number>(props: {
  value: V[];
  options: Array<{ label: string; value: V }>;
  onChange: (v: V[]) => void;
  placeholder?: string;
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
}) {
  const { value, options, onChange, placeholder, disabled, ariaLabel, className } = props;
  const showClear = value.length > 0 && !disabled;
  return (
    <Select<V[]>
      multiple
      className={className}
      value={value}
      displayEmpty
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as V[])}
      inputProps={{ "aria-label": ariaLabel ?? placeholder }}
      IconComponent={(p) => (
        <span {...p} style={{ ...p.style, top: 0, right: 0, height: "100%", width: "2.357rem", transform: "none", display: "grid", placeItems: "center", color: "#6c757d", pointerEvents: "none" }}>
          <PrimeGlyph name="chevron-down" />
        </span>
      )}
      endAdornment={
        showClear ? (
          <button
            type="button"
            aria-label="Clear"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => onChange([])}
            style={{ position: "absolute", right: "2.357rem", border: 0, padding: 0, background: "none", color: "#6c757d", cursor: "pointer", display: "grid" }}
          >
            <PrimeGlyph name="times" />
          </button>
        ) : undefined
      }
      renderValue={(v) =>
        v.length === 0 ? (
          <span style={{ color: "#6c757d" }}>{placeholder}</span>
        ) : (
          options
            .filter((o) => v.includes(o.value))
            .map((o) => o.label)
            .join(", ")
        )
      }
      MenuProps={{ slotProps: { paper: { sx: { borderRadius: "4px", mt: 0.25, boxShadow: "0 2px 4px -1px rgba(0,0,0,.2), 0 4px 5px 0 rgba(0,0,0,.14), 0 1px 10px 0 rgba(0,0,0,.12)" } } } }}
      sx={{
        width: "100%",
        fontSize: "1rem",
        borderRadius: "4px",
        "&.Mui-disabled": { opacity: 0.6 },
        // MUI SIZES `.MuiSelect-select.MuiInputBase-input` TO 1.4375EM; THE DOUBLED CLASS OUTRANKS
        // IT SO THE LABEL IS ONE 14PX METROPOLIS LINE AND THE FIELD 30PX, LIKE `p-dropdown`. MUI
        // DRAWS THE BORDER AS AN OVERLAY INSIDE THE BOX, SO THE PADDING CARRIES ITS 1PX
        "& .MuiSelect-select.MuiSelect-select": {
          height: "auto",
          minHeight: 0,
          padding: "calc(0.5rem + 1px) calc(0.75rem + 1px)",
          paddingRight: showClear ? "4.357rem !important" : "2.357rem !important",
          lineHeight: "normal",
        },
      }}
    >
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value} sx={{ fontSize: "1rem", padding: "0.5rem 0.75rem", minHeight: "auto", gap: "0.5rem" }}>
          <Checkbox checked={value.includes(o.value)} size="small" sx={{ p: 0 }} disableRipple />
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
}
